"use client";

import { useEffect, useState } from "react";
import mainAPI from "../../../services/main/mainAPI";

type RankingRow = {
  game_type: string;
  create_game_count: number;
};

const formatGameType = (rawType: string): string => {
  const typeMap: Record<string, string> = {
    "find_the_match": "Find the match",
    "flappy_bird": "Flappy Chicken",
    "whack_a_mole": "Whack a mole",
    "anagram": "Anagram",
    "flip_cards": "Flip cards",
    "air_plan/flying_fruits": "Flying Fruits",
    "complete_the_sentence": "Complete the sentence",
    "situation": "Situation",
    "game_show_quiz": "Game show Quiz",
  };

  if (typeMap[rawType]) {
    return typeMap[rawType];
  }

  const formatted = rawType.replace(/_/g, " ");
  return formatted.charAt(0).toUpperCase() + formatted.slice(1);
};

function normalizeRankingRows(response: unknown): RankingRow[] {
  const sourceRows = Array.isArray(response)
    ? response
    : Array.isArray((response as { data?: unknown[] })?.data)
      ? (response as { data: unknown[] }).data
      : [];

  return sourceRows
    .map((item) => {
      const row = item as { game_type?: unknown; create_game_count?: unknown; createGameCount?: unknown };
      const rawGameType = String(row.game_type ?? "").trim();
      const rawCount = row.create_game_count ?? row.createGameCount;
      const count = Number(rawCount);

      if (!rawGameType || !Number.isFinite(count)) {
        return null;
      }

      return {
        game_type: formatGameType(rawGameType),
        create_game_count: count,
      };
    })
    .filter(Boolean) as RankingRow[];
}

export default function RecommendGame() {
  const [rows, setRows] = useState<RankingRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadRanking = async () => {
    setIsLoading(true);
    const response = await mainAPI.getAllGamePopularCreateGameRanking();

    if (!response || (response as { status?: string })?.status === "failed") {
      setRows([]);
      setIsLoading(false);
      return;
    }

    setRows(normalizeRankingRows(response));
    setIsLoading(false);
  };

  useEffect(() => {
    void loadRanking();
  }, []);

  const renderTable = (tableRows: RankingRow[]) => {
    if (isLoading) {
      return <div className="px-4 py-6 text-sm text-[#7a7a7a]">กำลังโหลด...</div>;
    }

    if (tableRows.length === 0) {
      return <div className="px-4 py-6 text-sm text-[#7a7a7a]">ไม่พบข้อมูล</div>;
    }

    return (
      <div className="overflow-x-auto px-4 pb-3">
        <table className="min-w-full border-separate border-spacing-y-0">
          <thead>
            <tr className="text-left text-sm font-semibold text-[#5a5a5a]">
              <th className="w-14 px-4 py-2"></th>
              <th className="px-4 py-2">ชื่อเกม</th>
              <th className="w-28 px-4 py-2 text-right">จำนวนการใช้</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, index) => {
              const rowNumber = index + 1;
              const isEven = index % 2 === 1;

              return (
                <tr
                  key={`${row.game_type}_${rowNumber}`}
                  className={isEven ? "bg-[#ECEFF6]" : "bg-[#F7F7F7]"}
                >
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">{rowNumber}</td>
                  <td className="px-4 py-2 text-lg font-normal text-[#4E4E4E]">{row.game_type}</td>
                  <td className="px-4 py-2 text-right text-lg font-normal text-[#4E4E4E]">{row.create_game_count}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <h1 className="text-lg font-bold text-[#4E4E4E]">ประเภทเกมยอดนิยม</h1>
      </div>
      {renderTable(rows)}
    </div>
  );
}